import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import Alert from '@material-ui/lab/Alert';
import { Color } from '@material-ui/lab/Alert/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Account } from '../hooks/useAccount';
import { dateFormat } from '../styles';
import { theme } from '../styles/theme';
import { Product, ProductReferral } from '../swagger';
import { isHealthCheckup } from '../utils/isHealthCheckup';

// The same icons MUI's Alert shows, so the one-line form reads as the same
// thing as the box.
const icons: Record<Color, React.ElementType> = {
  success: CheckCircleOutlineIcon,
  info: InfoOutlinedIcon,
  warning: ReportProblemOutlinedIcon,
  error: ErrorOutlineIcon,
};

/**
 * The engine reports `expired` — the checkup's paid period is over — and dates
 * it with `validUntil`. Neither is in `src/swagger` yet: the client is
 * generated against a running engine (the `api-client`
 * skill). Until the next `pnpm generate`, widen the type here; after it, this
 * block and the cast in `getReferral` go and `ProductReferral` covers it.
 */
const EXPIRED = 'expired' as const;
type ReferralStatus = ProductReferral.StatusEnum | typeof EXPIRED;
type Referral = Omit<ProductReferral, 'status'> & {
  status: ReferralStatus;
  validUntil?: string;
};
const getReferral = (product: Product): Referral | undefined =>
  product.referral as Referral | undefined;

const severities: Record<ReferralStatus, Color> = {
  [ProductReferral.StatusEnum.Pending]: 'info',
  [ProductReferral.StatusEnum.Active]: 'success',
  [ProductReferral.StatusEnum.Sampled]: 'info',
  [ProductReferral.StatusEnum.Completed]: 'success',
  [EXPIRED]: 'warning',
  [ProductReferral.StatusEnum.Failed]: 'error',
  [ProductReferral.StatusEnum.Cancelled]: 'info',
};

/**
 * We date the referral only where the date is about the referral itself. Once
 * the blood has been drawn the send date is history, and while it is still
 * being made there is nothing to date yet.
 */
const showsSentDate: ReferralStatus[] = [ProductReferral.StatusEnum.Active];

const showsCity: ReferralStatus[] = [
  ProductReferral.StatusEnum.Pending,
  ProductReferral.StatusEnum.Active,
];

/**
 * Whether the referral was issued, is still usable, or lapsed is logistics: a
 * company that bought forty checkups has every reason to see which ones got
 * used. That the person has since given blood, or that their results are in,
 * describes the care they received rather than the referral — so those two
 * stay with the person the checkup is for.
 */
const careStatuses: ReferralStatus[] = [
  ProductReferral.StatusEnum.Sampled,
  ProductReferral.StatusEnum.Completed,
];

interface OrderProductReferralProps {
  product: Product;
  /**
   * One line instead of the alert box, for the order overview card where the
   * checkup already shares a compact block with its name, period and id.
   */
  compact?: boolean;
}

/**
 * The e-referral behind a health checkup: whether it can be used at a lab,
 * until when, and which town was picked.
 *
 * `GET /account` returns every order the caller owns *or pays for*, so a
 * company payer receives its employees' checkup products too. It gets the
 * logistics — issued, usable, lapsed — but not `careStatuses`, and not the
 * instructions, which are written to the patient in the second person.
 */
export const OrderProductReferral: React.FC<OrderProductReferralProps> = ({
  product,
  compact,
}) => {
  // The locale comes from react-intl rather than the Settings container: the
  // date sits inside a translated sentence, so it must follow the same locale
  // that sentence was rendered in.
  const { locale } = useIntl();
  const { account } = Account.useContainer();
  const referral = getReferral(product);
  const isOwnCheckup = !!account?.id && account.id === product.account?.id;

  if (!isHealthCheckup(product.type) || !referral) {
    return null;
  }

  const { status, createdAt, validUntil, city } = referral;

  if (!isOwnCheckup && careStatuses.includes(status)) {
    return null;
  }

  const formatDate = (iso?: string) =>
    iso && DateTime.fromISO(iso, { zone: 'utc' }).isValid
      ? DateTime.fromISO(iso, { zone: 'utc' }).toLocaleString({
          ...dateFormat,
          locale,
        })
      : undefined;
  // The one date that matters for each state: when it was sent while it is
  // live, and the checkup's last paid day once that has passed.
  const dateLine =
    status === EXPIRED
      ? { id: 'referral.expired_at', date: formatDate(validUntil) }
      : showsSentDate.includes(status)
      ? { id: 'referral.sent_at', date: formatDate(createdAt) }
      : undefined;
  const sent = dateLine?.date ? dateLine : undefined;

  const severity = severities[status];
  // "Din remiss" is addressed to the patient. A payer is reading about someone
  // else's, so they get the third-person wording.
  const title = (
    <FormattedMessage
      id={
        isOwnCheckup
          ? `referral.status.${status}`
          : `referral.other.status.${status}`
      }
    />
  );

  if (compact) {
    const Icon = icons[severity];

    return (
      <Typography
        variant="body2"
        style={{
          marginBottom: 0,
          color: theme.palette[severity].main,
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(0.5),
        }}
      >
        <Icon fontSize="inherit" />
        <span>
          {title}
          {!!sent && (
            <>
              {' · '}
              <FormattedMessage
                id={`${sent.id}_short`}
                values={{ date: sent.date }}
              />
            </>
          )}
        </span>
      </Typography>
    );
  }

  return (
    <Box pb={2}>
      <Alert severity={severity}>
        <AlertTitle>{title}</AlertTitle>
        {isOwnCheckup && <FormattedMessage id={`referral.body.${status}`} />}
        {!!sent && (
          <Box pt={1}>
            <strong>
              <FormattedMessage id={sent.id} values={{ date: sent.date }} />
            </strong>
          </Box>
        )}
        {showsCity.includes(status) && !!city && (
          <Box>
            <FormattedMessage id="referral.city" values={{ city }} />
          </Box>
        )}
      </Alert>
    </Box>
  );
};
